import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaEnvelope,
  FaRegCommentDots,
  FaPaperPlane,
} from "react-icons/fa";

interface FormData {
  name: string;
  email: string;
  message: string;
}

const ContactForm = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  // hide messages after a short time
  useEffect(() => {
    if (submitStatus === "success" || submitStatus === "error") {
      const t = setTimeout(() => setSubmitStatus("idle"), 7000);
      return () => clearTimeout(t);
    }
  }, [submitStatus]);

  // accept input from input and textarea
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    // enforce max length for message
    if (name === "message" && value.length > 1000) return;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
        credentials: "include",
      });

      if (res.status === 200) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", message: "" });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-primary text-white px-6 py-10">
      <div className="max-w-screen-xl mx-auto">
        <h2 className="text-[45px] font-bold font-sora mb-1">Talk To Us</h2>
        <p className="mb-6 font-sans text-base">Want to ask us a question.</p>

        <form onSubmit={handleSubmit}>
          {/* responsive layout: stacked on small, single-row on md+ */}
          <div className="flex flex-col md:flex-row md:items-start gap-4 mb-6">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
              {/* Name */}
              <div className="col-span-1">
                <div className="flex items-center h-14 rounded-full bg-gradient-to-b from-[#5F84FF] to-[#3F6AEF] px-4">
                  <input
                    type="text"
                    name="name"
                    aria-label="Name"
                    placeholder="Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="flex-1 bg-transparent text-white placeholder-white outline-none h-full"
                  />
                  <span
                    className="ml-4 text-white pointer-events-none"
                    aria-hidden="true"
                  >
                    <FaUser />
                  </span>
                </div>
              </div>

              {/* Email */}
              <div className="col-span-1">
                <div className="flex items-center h-14 rounded-full bg-gradient-to-b from-[#5F84FF] to-[#3F6AEF] px-4">
                  <input
                    type="email"
                    name="email"
                    aria-label="Email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="flex-1 bg-transparent text-white placeholder-white outline-none h-full"
                  />
                  <span
                    className="ml-4 text-white pointer-events-none"
                    aria-hidden="true"
                  >
                    <FaEnvelope />
                  </span>
                </div>
              </div>

              {/* Message (use textarea) */}
              <div className="col-span-1">
                <div className="flex items-center h-14 rounded-full bg-gradient-to-b from-[#5F84FF] to-[#3F6AEF] px-4">
                  <input
                    name="message"
                    aria-label="Message"
                    placeholder="Message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={1000}
                    type="text"
                    className="flex-1 bg-transparent text-white placeholder-white outline-none h-full"
                  />
                  <span
                    className="ml-4 text-white pointer-events-none"
                    aria-hidden="true"
                  >
                    <FaRegCommentDots />
                  </span>
                </div>
              </div>
            </div>

            {/* submit column */}
            <div className="flex md:flex-col items-center md:items-end gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-white text-primary h-14 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                    Sending...
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Send</span>
                    <FaPaperPlane />
                  </>
                )}
              </button>

              {/* status message — kept outside inputs so layout doesn't break */}
              <div aria-live="polite" className="min-h-[1.25rem] text-sm">
                {submitStatus === "success" && (
                  <p className="text-green-300 font-sans text-base">
                    Message sent successfully!
                  </p>
                )}
                {submitStatus === "error" && (
                  <p className="text-red-300 font-sans text-base">
                    Failed to send message. Please try again.
                  </p>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
